import { transactionDataType } from "../../../models/types/transactionData.types";
import { TransactionView } from "../../../models/view/transaction/transaction.view";
import {
  IReferenceData,
  ReferenceDataSource,
  ReferenceDataView,
  toReferenceDataView,
} from "../../../models/view/reference-data/reference-data.view";
import { TransactionStatusEnum } from "../../../models/enum/transactionType";
import { TransactionDirection } from "../../../models/enum/transactionDirection";
import {
  enrichTransaction,
  newAllocationWithNewCategory,
} from "../../../models/view/transaction/transaction.utils";
import { AllocationUtils } from "../../../models/store/allocations/allocation.utils";
import { GlossDateFactory } from "../../../models/data/shared/gloss-date/gloss-date.factory";
import { GlossQuantity, GlossQuantityUtils } from "../../../models/data/shared/gloss-quantity";
import { GlossNumberUtils } from "../../../models/data/shared/gloss-number";
import { NotificationEnum } from "../../../models/enum/notification.enum";
import {
  TransactionDetailsComponent,
  TransactionDetailType,
} from "../../../gloss/manage/manage-accounts/transaction-details/transaction-details.component";
import { Injector } from "@angular/core";
import { firstValueFrom, filter, map, timeout } from "rxjs";
import { ConfirmationDialogService } from "../../../services/confirmation/confirmation.service";
import { TransactionConversionService } from "../../../services/DataService/transaction/transaction-conversion.service";
import { TransactionStoreService } from "../../../services/store/transaction/transaction.store.service";
import { AccountView } from "../../../models/view/account/account.view";
import { MatDialog } from "@angular/material/dialog";
import { clone } from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
export class TransactionDataHelper {
  private notifyService: ConfirmationDialogService;
  private transactionStore: TransactionStoreService;
  private transactionConversionService: TransactionConversionService;
  private dialog: MatDialog;

  constructor(injector: Injector) {
    this.notifyService = injector.get(ConfirmationDialogService);
    this.transactionStore = injector.get(TransactionStoreService);
    this.transactionConversionService = injector.get(TransactionConversionService);
    this.dialog = injector.get(MatDialog);
  }

  private stockFormToTransactionView(
    $event: transactionDataType,
    existingTransaction: TransactionView,
  ): [TransactionView, ReferenceDataView, any] {
    // Capture before the rebuild below clobbers it (see fromToGlossQuantity's previousQuantity).
    const previousQuantity = existingTransaction?.quantity;

    const allocationAmount = $event.formData.quantity;
    const newTransaction: TransactionView = this.assignTransactionDataFromManualForm(
      $event,
      existingTransaction,
      allocationAmount,
    );

    /** Set the valuation of the transaction **/
    const transactionCurrency = $event.formData.currency; // the currency of the rate per unit, e.g 'HKD'
    const transactionSymbolAmount = $event.formData.quantity; // how many units of the symbol we have
    const transactionSymbol = $event.formData.symbol; // the symbol of the transaction, e.g. 'BHP'

    newTransaction.quantity = this.fromToGlossQuantity(
      transactionSymbolAmount,
      transactionSymbol,
      transactionCurrency,
      previousQuantity,
    );

    let editedSymbol;

    if (existingTransaction) {
      const symbol = this.transactionStore.symbols
        .collection()
        .find((s) => s.code === transactionSymbol);

      editedSymbol = clone(symbol);
      editedSymbol.code = transactionSymbol;
      editedSymbol.sources[ReferenceDataSource.MANUAL] = transactionSymbol;
      editedSymbol.dateModified = new Date();
    }

    const rate = $event.formData.amount; // the rate of the symbol per unit
    // valuationPrice lets the system know the unit price of the symbol when the transaction was created
    newTransaction.valuationPrice = Number(rate);

    const referenceData: IReferenceData = {
      base: transactionSymbol,
      date: {
        date: newTransaction.glossPostDate.dateString,
        time: null,
        timeZone: null,
        reliableTimeSource: true,
      },
      symbol: transactionCurrency,
      rate: rate,
      source: ReferenceDataSource.MANUAL, // align with the transaction origin
    };

    enrichTransaction(newTransaction);

    return [newTransaction, toReferenceDataView(referenceData), editedSymbol];
  }

  private bankFormToTransactionView(
    $event: transactionDataType,
    existingTransaction: TransactionView,
  ): TransactionView {
    // Capture before the rebuild below clobbers it (see fromToGlossQuantity's previousQuantity).
    const previousQuantity = existingTransaction?.quantity;

    const allocationAmount = $event.formData.amount;
    const newTransaction: TransactionView = this.assignTransactionDataFromManualForm(
      $event,
      existingTransaction,
      allocationAmount,
    );

    /** Set the valuation of the transaction **/
    const transactionCurrency = $event.formData.currency;
    // #4590: keep the transaction's own symbol (the dialog seeds formData.symbol from
    // quantity.actualQuantity.symbol). Passing the currency as the symbol rewrote it on
    // any edit — silently auto-unlinking a transfer/conversion pair where symbol !== currency
    // (reachable via CSV import). Fall back to the currency only when no symbol is present.
    const transactionSymbol = $event.formData.symbol || transactionCurrency;
    const transactionValue = $event.formData.amount; // value = amount * rate ( currency/symbol )

    newTransaction.quantity = this.fromToGlossQuantity(
      transactionValue,
      transactionSymbol,
      transactionCurrency,
      previousQuantity,
    );

    enrichTransaction(newTransaction);

    return newTransaction;
  }

  private assignTransactionDataFromManualForm(
    $event: transactionDataType,
    existingTransaction: TransactionView,
    allocationAmount: number,
  ): TransactionView {
    const newTransaction: TransactionView = existingTransaction ?? new TransactionView();

    newTransaction.origin = "manual";
    newTransaction.definition = TransactionStatusEnum.transaction;
    newTransaction.accountLink = {
      id: $event.account.id,
    };
    newTransaction.accountId = $event.account.id;
    newTransaction.description = $event.formData.description;

    const signedAllocationAmount =
      $event.formData.type === TransactionDirection.Out ? allocationAmount * -1 : allocationAmount;
    newTransaction.allocations = $event.formData.categoryId
      ? newAllocationWithNewCategory($event.formData.categoryId, signedAllocationAmount)
      : AllocationUtils.createDefaultAllocations(signedAllocationAmount);
    newTransaction.direction = $event.formData.type;
    newTransaction.glossPostDate = GlossDateFactory.createFromDateString(
      $event.formData.date,
      $event.formData.time,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      true,
    );

    return newTransaction;
  }

  fromToGlossQuantity(
    quantityAmount: number,
    quantitySymbol: string,
    quantityCurrency: string,
    // The pre-edit quantity, captured by each *FormToTransactionView caller BEFORE
    // assignTransactionDataFromManualForm returns the same reference and the quantity reassignment
    // clobbers it — so a supplied conversion rate can still be read (and preserved) here.
    previousQuantity?: GlossQuantity,
  ): GlossQuantity {
    // #4443/#4592: this rebuild would otherwise reset the conversion fields, wiping an imported
    // conversion rate on any edit (incl. description-only). Preserve what the transaction already
    // carried so both the stock and bank paths inherit it and can't drift — but only while the rate
    // still describes the currency being written: convrate converts quantityCurrency into convsym,
    // so a currency change makes the stored rate meaningless and it must fall back to reference data.
    const preserved =
      previousQuantity?.currency === quantityCurrency ? previousQuantity : undefined;

    return GlossQuantityUtils.fromSelfProperties({
      actualQuantity: GlossNumberUtils.fromSelfProperties({
        amount: quantityAmount,
        symbol: quantitySymbol,
        precision: 8,
      }),
      currency: quantityCurrency, // the currency of the rate per unit
      convrate: preserved?.convrate ?? null, // can be used to supply a conversion rate if the transactionCurrency is different from the core currency
      convsym: preserved?.convsym ?? "", // can be used to supply the conversion rate currency
    } as GlossQuantity);
  }

  async receivedTransactionData(
    receivedData: transactionDataType | "touched",
    popTransactionDetail: boolean = false,
    existingTransaction: TransactionView = null,
  ) {
    if (receivedData && receivedData !== "touched") {
      const selectedAccount = receivedData.account;
      const selectedAccountType = selectedAccount.type;
      let newTransaction: TransactionView;
      let referenceData: ReferenceDataView;
      let editedSymbol;
      try {
        if (selectedAccountType === "stock" || selectedAccountType === "other") {
          [newTransaction, referenceData, editedSymbol] = this.stockFormToTransactionView(
            receivedData,
            existingTransaction,
          );
          // todo @alex use the saveTovault method for not double triggering
          await this.transactionStore.referenceData.save(referenceData);
          if (editedSymbol) {
            await this.transactionStore.symbols.save(editedSymbol);
          }
        } else if (selectedAccountType === "bank" || selectedAccountType === "credit") {
          newTransaction = this.bankFormToTransactionView(receivedData, existingTransaction);
        } else {
          this.notifyService.notify(
            NotificationEnum.error,
            "error",
            "Account type Not implemented yet",
          );
          return;
        }

        const unlinkedPartner = this.autoUnlinkIfConditionsBroken(newTransaction);
        if (unlinkedPartner) {
          // One vault write, one triggerChanges: both sides of the link land together (#293).
          await this.transactionStore.transaction.saveToVault(
            [unlinkedPartner, newTransaction],
            true,
          );
        } else {
          await this.transactionStore.transaction.save(newTransaction);
        }

        if (popTransactionDetail) {
          const enrichedTransaction = await firstValueFrom(
            this.transactionStore.enrichTransaction.collection$.pipe(
              filter((transactions) => !!transactions),
              map((transactions) => transactions.find((t) => t.id === newTransaction.id)),
              filter((t) => !!t),
              timeout(10000),
            ),
          );
          const rowData: TransactionDetailType = this.setTransactionDetailData(
            enrichedTransaction,
            selectedAccount,
          );
          TransactionDetailsComponent.open(rowData, this.dialog);
        }
      } catch (e) {
        this.notifyService.notify(NotificationEnum.error, "error", "transactionFailed");
      }
    }
  }

  /**
   * Breaks a persisted transfer/conversion link when an edit has made the pair no
   * longer meet the linking conditions (issue #293). Clears `linkedTo` on the edited
   * transaction in place and returns the partner clone for the caller to persist
   * alongside it in a single write, or null if there is nothing to unlink.
   * `excludedLinkIds` is deliberately left untouched: a conditions failure is not a
   * user veto, so the pair stays eligible for future auto-detection if a later edit
   * makes it valid again.
   */
  private autoUnlinkIfConditionsBroken(edited: TransactionView): TransactionView | null {
    const partnerId = edited.linkedTo?.[0];
    if (!partnerId) {
      return null;
    }

    const partner = (this.transactionStore.transaction.transactionViews() ?? []).find(
      (transaction) => transaction.id === partnerId,
    );
    if (!partner || this.transactionConversionService.isStillLinkable(edited, partner)) {
      return null;
    }

    edited.linkedTo = [];
    edited.c_linkType = null;

    // Store views are frozen; reassign (never mutate) linkedTo on a clone.
    const unlinkedPartner = partner.clone();
    unlinkedPartner.linkedTo = [];
    return unlinkedPartner;
  }

  setTransactionDetailData(transactionView: TransactionView, selectedAccount: AccountView) {
    return {
      transactionView: transactionView,
      accountView: selectedAccount,
      transactionDate: transactionView.glossPostDate.dateString,
      description: transactionView.description,
      institution: selectedAccount.c_institution.name,
      account: selectedAccount.name,
      source: selectedAccount.source,
    };
  }
}
